/**
 * Contains a SlackController class that handles all Slack operations and is used as a singleton.
 * Does not render anything.
 */
import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";

import ReconnectingWebSocket from "reconnecting-websocket";
import { deepChange, getWebsocketAddress, goToModule, scrollBottom } from "../../actions";
import { getActiveModule, getLatestState, getSlackAnimationStatus } from "../../store";
import { MessageTypes, ModuleTypes, SlackAnimationStatus } from "../../lib/types";
import { addMessage } from "../messages/actions";
import { MessageWrapper, MessageBubble } from "../messages/BotMessage";
import { TypingAnimation } from "../../lib/animations";

class SlackController {
  constructor() {
    this.dispatch = null;
    this.getState = null;
    this.ws = null;
  }

  saveDispatch = (dispatch) => {
    this.dispatch = dispatch;
  };

  connect = () => {
    this.ws = new ReconnectingWebSocket(this.dispatch(getWebsocketAddress));
    this.initListeners();
  };

  close = () => {
    console.log("Closing websocket intentionally.");
    this.ws.close();
  };

  initListeners = () => {
    this.ws.onopen = this.onOpen;
    this.ws.onmessage = this.onMessage;
    this.ws.onclose = this.onClose;
  };

  /**
   * Called when websocket connection is successfully established.
   * @param e
   */
  onOpen = (e) => {
    console.log("Websocket connection established.");
  };

  /**
   * Handler for new websocket message received.
   * @param e
   */
  onMessage = (e) => {
    const data = JSON.parse(e.data);
    const message = data.message;
    console.log("New message:", message);

    this.dispatch(
      addMessage({
        type: MessageTypes.EXTRA_BOT_MESSAGE,
        text: message,
      })
    );
  };

  /**
   * Handler for websocket close event.
   * @param e
   */
  onClose = (e) => {
    console.error("Chat socket closed");
  };

  /**
   * Send message to websocket server.
   * @param messageText
   */
  sendMessage = (messageText) => {
    this.ws.send(
      JSON.stringify({
        message: messageText,
      })
    );
  };

  /**
   * Create a Timeout handle that should start the typing animation later.
   * @returns {number}
   */
  initDotsTimeout = () => {
    return setTimeout(this.startDotsAnimation, 5000);
  };

  /**
   * Actually start the typing animation.
   */
  startDotsAnimation = () => {
    this.dispatch(
      deepChange({
        "slackAnimation.typingAnimation": SlackAnimationStatus.show,
      })
    );
    scrollBottom();
  };

  /**
   * Create a Timeout handle that should switch the chat to the action of the Slack module.
   * @returns {number}
   */
  initActionTimeout = () => {
    return setTimeout(this.actionTimeout, 30000);
  };

  /**
   * Navigate Chat to the action of the Slack module.
   */
  actionTimeout = () => {
    const state = this.dispatch(getLatestState);
    const activeModule = getActiveModule(state);

    this.dispatch(
      deepChange({
        "slackAnimation.typingAnimation": SlackAnimationStatus.off,
      })
    );
    if (activeModule.type === ModuleTypes.slack) {
      // Should always be true, but double checking just in case.
      this.dispatch(goToModule(activeModule.action));
    }
  };
}

const slackController = new SlackController();

/**
 * Some weird singleton magic.
 * @param dispatch
 * @returns {SlackController}
 * TODO: use `import { useDispatch } from "react-redux";` instead
 * of passing `dispatch` like this.
 */
export default (dispatch) => {
  slackController.saveDispatch(dispatch);
  return slackController;
};

/**
 * Render animated dots to simulate like if somebody in Slack is typing in an answer.
 */
export const SlackTypingAnimation = () => {
  const slackAnimationStatus = useSelector(getSlackAnimationStatus);

  if (slackAnimationStatus.typingAnimation === SlackAnimationStatus.show) {
    return (
      <MessageWrapper>
        <MessageBubble showTypingAnimation={true}>
          <TypingAnimation />
        </MessageBubble>
      </MessageWrapper>
    );
  }
  return null;
};
